import type { SidecarRecord } from '@/types/intake';

/**
 * Client-side sequencing for the Review queue (sidecar records). The review list arrives in one
 * payload, so re-sorting never re-hits the API. Four keys, each ascending or descending: member
 * name (last name with a full-name fallback), arrival (USPS received-at-lab, receivedAt), return
 * tracking no., member id. Null / empty values always sort to the END (regardless of direction).
 * Parallel to queue-sort.ts.
 */

export type SidecarSortField = 'name' | 'arrival' | 'tracking' | 'member';
export type SidecarSortDir = 'asc' | 'desc';

export const SIDECAR_SORT_LABELS: Record<SidecarSortField, string> = {
  name: 'Name',
  arrival: 'Arrival date',
  tracking: 'Tracking no.',
  member: 'Member ID',
};

export const SIDECAR_SORT_FIELDS = Object.keys(SIDECAR_SORT_LABELS) as SidecarSortField[];

const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V']);

/** Last name from the snapshot full name (the sidecar has no discrete last-name column). */
export function lastNameKey(r: SidecarRecord): string {
  const parts = (r.memberNameSnapshot ?? '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  while (parts.length > 1 && NAME_SUFFIXES.has(parts[parts.length - 1]!)) parts.pop();
  return parts.length ? parts[parts.length - 1]! : '';
}

/**
 * Stable-ish ascending comparison for one field. Empty / null keys sort AFTER non-empty keys so
 * that, in either direction, records with no value land at the bottom of the list. Direction is
 * applied by the caller (sortSidecars) only to the non-empty comparison.
 */
export function compareSidecars(a: SidecarRecord, b: SidecarRecord, field: SidecarSortField): number {
  switch (field) {
    case 'name': {
      const la = lastNameKey(a);
      const lb = lastNameKey(b);
      const e = emptyOrder(la, lb);
      if (e !== null) return e;
      const byLast = la.localeCompare(lb);
      if (byLast !== 0) return byLast;
      return (a.memberNameSnapshot ?? '').toUpperCase().localeCompare((b.memberNameSnapshot ?? '').toUpperCase());
    }
    case 'arrival': {
      const va = a.receivedAt ?? '';
      const vb = b.receivedAt ?? '';
      const e = emptyOrder(va, vb);
      if (e !== null) return e;
      return va.localeCompare(vb);
    }
    case 'tracking': {
      const va = a.returnTracking ?? '';
      const vb = b.returnTracking ?? '';
      const e = emptyOrder(va, vb);
      if (e !== null) return e;
      return va.localeCompare(vb);
    }
    case 'member': {
      const va = (a.memberId ?? '').trim();
      const vb = (b.memberId ?? '').trim();
      const e = emptyOrder(va, vb);
      if (e !== null) return e;
      const na = Number(va);
      const nb = Number(vb);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return va.localeCompare(vb);
    }
  }
}

/**
 * Empty-to-end ordering helper. Returns a fixed comparison (empty sorts after non-empty) when
 * exactly one side is empty, or 0 when both are empty; null when neither is empty (defer to the
 * field comparison). The returned values are NOT scaled by direction, so empties stay at the end
 * for both asc and desc.
 */
function emptyOrder(a: string, b: string): number | null {
  const ea = a === '';
  const eb = b === '';
  if (ea && eb) return 0;
  if (ea) return 1;
  if (eb) return -1;
  return null;
}

/** Return a new array sorted by field + direction (does not mutate the input). Empties stay last. */
export function sortSidecars(items: SidecarRecord[], field: SidecarSortField, dir: SidecarSortDir): SidecarRecord[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const cmp = compareSidecars(a, b, field);
    // Empty-to-end results are returned by emptyOrder as +/-1 with one side empty; keep those fixed
    // (never flipped by direction) so null/empty keys remain at the bottom in both directions.
    const oneEmpty = isEmptyForField(a, field) !== isEmptyForField(b, field);
    if (oneEmpty) return cmp;
    return factor * cmp;
  });
}

/** True when the record's key for `field` is null / empty (used to pin empties to the end). */
function isEmptyForField(r: SidecarRecord, field: SidecarSortField): boolean {
  switch (field) {
    case 'name':
      return lastNameKey(r) === '';
    case 'arrival':
      return (r.receivedAt ?? '') === '';
    case 'tracking':
      return (r.returnTracking ?? '') === '';
    case 'member':
      return (r.memberId ?? '').trim() === '';
  }
}
